import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';
import { PrintersStatus } from 'src/app/models/printing.model';
import { PrintService } from 'src/app/common/services/print.service';
import { ApiService } from 'src/app/common/services/api.service';
import { LocalStorageService } from 'src/app/common/services/localStorage.service';
import { PrinterNameEnum} from '../components/enums/printer.enum';

@Injectable({
  providedIn: 'root',
})
export class PrinterStatusService implements OnDestroy {
  private allPrintersListSubject = new BehaviorSubject<PrintersStatus[]>([]);
  private allDelayPrintEventsSubject = new BehaviorSubject<PrintersStatus[]>(
    []
  );
  private allPrintersActiveSubject = new BehaviorSubject<boolean>(false);

  allPrintersList$ = this.allPrintersListSubject.asObservable();
  allPrintersActive$ = this.allPrintersActiveSubject.asObservable();
  allDelayPrintEvents$ = this.allDelayPrintEventsSubject.asObservable();

  allPrintersList: PrintersStatus[] = [];
  printerNameEnum = PrinterNameEnum;
  allPrintersActive: boolean = false;
  allDelayPrintEvents = [];
  kitchenCounterList: any[] = [];
  isRealmNotEnabledAndReactDevice: boolean = false;
  sortTimeoutRef: ReturnType<typeof setTimeout>;
  ngUnsubscribe = new Subject();
  alreadySubscribeDelayPrint: boolean = false;

  constructor(
    private _modalService: NzModalService,
    private _printService: PrintService,
    private _apiService: ApiService,
    private _localStorage: LocalStorageService,
    private _translateService: TranslateService
  ) {
    this._printService
      .getAllPrinterListData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        this.allPrintersActive = !this.checkAllPrintersStatus();
        const tempPrintersList = this.combinePrintersList(data);
        if (
          this.comparePrinterChanges(this.allPrintersList, tempPrintersList)
        ) {
          this.allPrintersList = [...tempPrintersList];
          this.allPrintersActive = !this.checkAllPrintersStatus();
          this.sortTimeoutRef = setTimeout(() => {
            this.sortPrinterList();
            this.updatePrinterActiveData();
          }, 1500);
        }
        this.updatePrinterActiveData();
      });

    this.watchRestSettings();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private watchRestSettings() {
    this._localStorage.watch('restaurant_settings').subscribe((value) => {
      this.getAllKitchenCounterListData();
      if (
        this.checkDelayPrintingEnabled() &&
        !this.alreadySubscribeDelayPrint
      ) {
        this.alreadySubscribeDelayPrint = true;
        this.subscribeAllDelayPrintEventsData();
      } else {
        this.alreadySubscribeDelayPrint = false;
      }
    });
  }

  private getAllKitchenCounterListData() {
    if (
      this._localStorage.get('token') &&
      (this.kitchenCounterList || []).length === 0
    ) {
      // fetch all kitchen counter list if it is not already fetched by API
      this.getAllPrinterList();
    }
  }

  public checkDelayPrintingEnabled(): boolean {
    let restSettings:any = {};
    try{
      restSettings = JSON.parse(
        this._localStorage.get('restaurant_settings') || '{}'
      );
    }catch(err){
      console.error(err);
    }
    return !!(restSettings?.global?.is_show_delay_print || 0);
  }

  public checkPrintStatusBtnVisible(): boolean {
    let restSettings: any = {};
    try {
      restSettings = JSON.parse(
        this._localStorage.get('restaurant_settings') || '{}'
      );
    } catch (err) {
      console.error(err);
    }
    return !!(restSettings?.print?.is_print_status_btn_visible || 0);
  }

  private subscribeAllDelayPrintEventsData() {
    this._printService
      .getAllDelayPrintEventsData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        this.allDelayPrintEvents = data || [];
        this.allDelayPrintEventsSubject.next(this.allDelayPrintEvents);
      });
  }

  private updatePrinterActiveData() {
    this.allPrintersListSubject.next(this.allPrintersList);
    this.allPrintersActiveSubject.next(this.allPrintersActive);
  }

  sortPrinterList() {
    this.allPrintersList.sort(
      (a: any, b: any) => a.isConnected - b.isConnected
    );
    clearTimeout(this.sortTimeoutRef);
    this.sortTimeoutRef = null;
  }

  checkAllPrintersStatus(): boolean {
    return this.allPrintersList.some(this.isAnyDisconnected);
  }

  isAnyDisconnected = (element: PrintersStatus) => !element.isConnected;

  checkAllPrintersDisconnected(): boolean {
    return this.allPrintersList.every(this.isAnyDisconnected);
  }

  totalDisconnectedPrinter(): number {
    const totalDisconnectedList = this.allPrintersList.filter(
      this.isAnyDisconnected
    );
    return totalDisconnectedList.length;
  }

  totalDelayPrintEvents(): number {
    return this.allDelayPrintEvents.length;
  }

  combinePrintersList(arr = []) {
    const result = [];

    arr.forEach((obj) => {
      // Find an existing printer in the result with the same IP address
      const existingPrinter = result.find((item) => item.ip === obj.ip);

      if (existingPrinter) {
        // If an existing printer with the same IP address is found,
        // and the conditions are met, update the is_eod property
        if (
          existingPrinter.id !== obj.id &&
          obj.id === this.printerNameEnum.EOD_PRINTER
        ) {
          existingPrinter.is_eod = true;
        }
      } else {
        // If no existing printer with the same IP address is found,
        // add the printer to the result array
        obj['printersNameObj'] = [];
        obj.printersName = this.generatePrinterNames(obj); // add new key for printers name
        result.push(obj);
      }
    });
    return result;
  }

  generatePrinterNames(printer) {
    const ip = (printer || {}).ip;
    let printerText = '';

    const addPrinterName = (name) => {
      if (printerText.length) {
        printerText += ', ';
      }
      printerText += name;
    };

    const masterPrinterName = this._translateService.instant(
      'PRINTER_STATUS.BILL_RECEIPT'
    );
    const eodReportName = this._translateService.instant(
      'PRINTER_STATUS.EOD_REPORT'
    );

    if (ip) {
      this.kitchenCounterList.forEach((counter) => {
        if (counter?.printer_name === ip) {
          const printerNameObj = {
            name: counter?.counter_name,
            status : counter?.status?.toString()
          }
          printer['printersNameObj'].push(printerNameObj);
          addPrinterName(counter?.counter_name);
        }
      });
    }

    if ((printer || {}).id === this.printerNameEnum.MASTER) {
      const printerNameObj = {
        name: masterPrinterName,
        status: '1'
      }
      printer['printersNameObj'].push(printerNameObj);

      addPrinterName(masterPrinterName);

      if ((printer || {}).is_eod) {
        const printerNameObj = {
          name: eodReportName,
          status: '1'
        }
        printer['printersNameObj'].push(printerNameObj);
        addPrinterName(eodReportName);
      }
    } else if ((printer || {}).id === 'eod_printer') {
      const printerNameObj = {
        name: eodReportName,
        status: '1'
      }
      printer['printersNameObj'].push(printerNameObj);
      addPrinterName(eodReportName);
    }

    return printerText;
  }

  getAllPrinterList() {
    const formData = new FormData();
    formData.append('token', this._localStorage.get('token'));
    this._apiService.getKitchenCounter(formData).subscribe((response) => {
      if ((response || {}).status === 1) {
        this.kitchenCounterList = (response || {}).kitchen_counters || [];
      }
    });
  }

  comparePrinterChanges(prev, curr) {
    const onlyInPrev = this.onlyInLeft(prev, curr, this.isSamePrinters);
    const onlyInCurr = this.onlyInLeft(curr, prev, this.isSamePrinters);
    const result = [...onlyInPrev, ...onlyInCurr];

    return !!result.length;
  }

  isSamePrinters = (a, b) => a.ip === b.ip && a.isConnected === b.isConnected;

  onlyInLeft = (left, right, compareFunction) =>
    left.filter(
      (leftValue) =>
        !right.some((rightValue) => compareFunction(leftValue, rightValue))
    );

  async openAllPrintersModal() {
    const { AllPrinterModalComponent } = await import(
      /* webpackPrefetch: true */ '../components/all-printer-modal/all-printer-modal.component'
    );
    const modal = this._modalService.create({
      nzContent: AllPrinterModalComponent,
      nzComponentParams: {
        allPrintersList: this.allPrintersList,
        allPrintersActive: this.allPrintersActive,
        allKitchenCounter: this.kitchenCounterList,
      },
      nzFooter: null,
      nzCentered: true,
      nzBodyStyle: { padding: '0px' },
      nzCloseIcon: null,
      nzWidth: 665,
    });
    return modal;
  }

  async openDelayPrintingModal() {
    const { DelayPrintingComponent } = await import(
      /* webpackPrefetch: true */ '../components/delay-printing/delay-printing.component'
    );
    const modal = this._modalService.create({
      nzContent: DelayPrintingComponent,
      nzBodyStyle: { padding: '0' },
      nzFooter: null,
      nzWidth: '85vw',
      nzClosable: false,
      nzCentered: true,
    });
    return modal;
  }

  isThisPrinterConnected(printerIp: string) {
    // check this printer ip is connected to device.
    return this.allPrintersList.some(
      (printer) => printer.ip === printerIp && printer.isConnected
    );
  }

  isOfflineDevice(): boolean {
     return this._printService.isRealmEnabled;
  }
}
